import { createSlice, PayloadAction, createAction } from '@reduxjs/toolkit';
import { globalActions } from '../../../app/model/actions';
import {
  NodeSelection,
  StartCommentAction,
  CreateCommentThreadAction,
  UpdateThreadStatusAction,
  DeleteThreadAction,
  DeleteCommentAction,
  UpdateCommentContent,
  CommentsFlow,
  StartCommentingFlowAction,
  MarkThreadAsDeletedAction,
  CreateCommentReplyAction,
} from './types';

type State = {
  isSideBarOpen: boolean;
  selection: NodeSelection | null;
  isCommentCreating: boolean;
  isCommentEditing: boolean;
  pendingThreads: string[];
  resolvedThreadIds: string[];
  commentsFlow: CommentsFlow | null;
  selectedThreadIds: string[];
  isCommentReplying: boolean;
};

const initialState: State = {
  isSideBarOpen: false,
  selection: null,
  isCommentCreating: false,
  isCommentEditing: false,
  pendingThreads: [],
  resolvedThreadIds: [],
  commentsFlow: null,
  selectedThreadIds: [],
  isCommentReplying: false,
};

export const { reducer, actions: reducerActions } = createSlice({
  name: 'comments',
  initialState,
  reducers: {
    startCommenting: (
      state: State,
      action: PayloadAction<StartCommentAction>
    ) => ({
      ...state,
      isPreSideBarOpen: true,
      selection: action.payload,
    }),
    endCommenting: (state: State) => ({
      ...state,
      isPreSideBarOpen: false,
      selection: null,
    }),
    setPendingThreads: (state, action: PayloadAction<string[]>) => {
      state.pendingThreads = action.payload;
    },
    setIsSideBarOpen: (state: State, action: PayloadAction<boolean>) => {
      state.isSideBarOpen = action.payload;
    },
    setIsCommentCreating: (state: State, action: PayloadAction<boolean>) => {
      state.isCommentCreating = action.payload;
    },
    setIsCommentEditing: (state: State, action: PayloadAction<boolean>) => {
      state.isCommentEditing = action.payload;
    },
    setResolvedThreadId: (state: State, action: PayloadAction<string[]>) => {
      state.resolvedThreadIds = action.payload;
    },
    updateSelectedThreads: (state: State, action: PayloadAction<string[]>) => ({
      ...state,
      selectedThreadIds: action.payload,
    }),
    setCommentsFlow: (
      state: State,
      action: PayloadAction<CommentsFlow | null>
    ) => {
      state.commentsFlow = action.payload;
    },
    setIsCommentReplying: (state: State, action: PayloadAction<boolean>) => {
      state.isCommentReplying = action.payload;
    },
  },
  extraReducers: (builder) =>
    builder.addCase(globalActions.resetStateAll, () => initialState),
});

export const actions = {
  ...reducerActions,
  createCommentThread: createAction<CreateCommentThreadAction>(
    'comments/createCommentThread'
  ),
  updateThreadStatus: createAction<UpdateThreadStatusAction>(
    'comments/updateThreadStatus'
  ),
  updateCommentContent: createAction<UpdateCommentContent>(
    'comments/updateCommentContent'
  ),
  deleteThread: createAction<DeleteThreadAction>('comments/deleteThread'),
  deleteComment: createAction<DeleteCommentAction>('comments/deleteComment'),

  startCommentingFlow: createAction<StartCommentingFlowAction>(
    'comments/startCommentingFlow'
  ),
  endCommentingFlow: createAction('comments/endCommentingFlow'),
  setSelectedThreads: createAction<string[]>('comments/setSelectedThreads'),
  commentThreadResolved: createAction<string>('comments/commentThreadResolved'),
  commentThreadDeleted: createAction<string>('comments/commentThreadDeleted'),
  markThreadAsDeleted: createAction<MarkThreadAsDeletedAction>(
    'comments/markThreadAsDeleted'
  ),
  createCommentReply: createAction<CreateCommentReplyAction>(
    'comments/createCommentReply'
  ),
};
